import { useMemo } from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { JsonCodeBlock } from '@/components/JsonCodeBlock'
import type { RelayEvalRun } from '../../types'

interface SessionAssistantModel {
  provider?: string
  model?: string
  messages?: Array<{ role?: string; content?: string }>
  tools?: Array<{ type?: string; function?: { name?: string } }>
}

interface SessionAssistant {
  model?: SessionAssistantModel
}

function isSessionAssistant(value: unknown): value is SessionAssistant {
  return Boolean(value && typeof value === 'object' && 'model' in value)
}

function getSessionConfig(run: RelayEvalRun) {
  const chatRunner = run.artifacts_json?.chat_runner
  if (!chatRunner || typeof chatRunner !== 'object') {
    return { dutyAssistant: null, counterpartyAssistant: null }
  }

  const dutyAssistant = (chatRunner as Record<string, unknown>)
    .duty_session_assistant
  const counterpartyAssistant = (chatRunner as Record<string, unknown>)
    .counterparty_session_assistant

  return {
    dutyAssistant: isSessionAssistant(dutyAssistant) ? dutyAssistant : null,
    counterpartyAssistant: isSessionAssistant(counterpartyAssistant)
      ? counterpartyAssistant
      : null,
  }
}

function SessionConfigPanel({
  label,
  assistant,
  showRawPayload,
}: {
  label: string
  assistant: SessionAssistant
  showRawPayload: boolean
}) {
  const model = assistant.model
  const rawSessionPayload = useMemo(() => ({ assistant }), [assistant])
  const modelId = [model?.provider, model?.model].filter(Boolean).join(':')
  const systemPrompt =
    model?.messages?.find((message) => message.role === 'system')?.content ?? ''
  const tools = model?.tools ?? []

  return (
    <AccordionItem value={label} className="border-gray-100">
      <AccordionTrigger className="py-3 text-sm font-medium text-primary-high-contrast hover:no-underline">
        {label}
      </AccordionTrigger>
      <AccordionContent className="space-y-3 pb-3">
        {modelId ? (
          <p className="text-xs text-neutral-low-contrast">
            <span className="font-semibold">Model: </span>
            <span className="font-mono">{modelId}</span>
          </p>
        ) : null}
        {tools.length > 0 ? (
          <div>
            <p className="text-xs font-semibold text-neutral-low-contrast">
              Tools ({tools.length})
            </p>
            <ul className="mt-1 flex flex-wrap gap-1">
              {tools.map((tool) => {
                const toolName = tool.function?.name ?? tool.type ?? 'unknown'
                return (
                  <li key={`${toolName}:${JSON.stringify(tool)}`}>
                    <Badge variant="outline" className="font-mono">
                      {toolName}
                    </Badge>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}
        {systemPrompt ? (
          <div>
            <p className="text-xs font-semibold text-neutral-low-contrast">
              System prompt
            </p>
            <div className="mt-1 max-h-64 overflow-y-auto rounded border border-gray-100 bg-gray-50 p-2.5">
              <pre className="whitespace-pre-wrap break-words text-xs leading-5 text-neutral-low-contrast">
                {systemPrompt}
              </pre>
            </div>
          </div>
        ) : null}
        {showRawPayload ? (
          <Accordion type="single" collapsible className="rounded border">
            <AccordionItem value="raw-session-payload" className="border-none">
              <AccordionTrigger className="px-2.5 py-2 text-xs font-semibold text-neutral-low-contrast hover:no-underline">
                Raw session payload
              </AccordionTrigger>
              <AccordionContent className="max-h-72 overflow-y-auto border-t px-2.5 py-2">
                <JsonCodeBlock
                  value={rawSessionPayload}
                  label={`${label} raw session payload`}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  )
}

export function SessionConfigSection({
  run,
  showRawPayload,
}: {
  run: RelayEvalRun
  showRawPayload: boolean
}) {
  const { dutyAssistant, counterpartyAssistant } = getSessionConfig(run)
  if (!dutyAssistant && !counterpartyAssistant) return null

  return (
    <Accordion type="multiple" className="mt-4 border-t border-gray-100">
      {dutyAssistant ? (
        <SessionConfigPanel
          label="Agent config"
          assistant={dutyAssistant}
          showRawPayload={showRawPayload}
        />
      ) : null}
      {counterpartyAssistant ? (
        <SessionConfigPanel
          label="Persona config"
          assistant={counterpartyAssistant}
          showRawPayload={showRawPayload}
        />
      ) : null}
    </Accordion>
  )
}
